import {
  ITableGroupBysForReportsDashboard,
  ITableMetrics,
  ORDER_DIRECTION,
  valueTypes,
} from '~/src/dash/components/tables/common';
import {
  IReportsDashboardConfig,
  IReportsDashboardFilters,
} from '~/src/dash/views/ias-publisher/IASReportsDashboard/ReportsDashboard/common/types';

export const formatComparisonKeys = (keys: string[], apiKey: string) => {
  switch (apiKey) {
    // convert to string with no escapes
    case 'AdvertiserName':
    case 'OrderName':
    case 'LineItemName':
    case 'AdUnitName':
      return keys.join(',');
    default:
      return null;
  }
};

const CUSTOMIZED_METRICS_CATEGORIES = {
  SUMMARY: 'Summary',
  AD_REFRESH: 'Ad Refresh',
  EXPOSURE: 'Exposure',
  GROUPM_VIEWABILITY: 'Group M Viewability',
  PUBLICIS_VIEWABILITY: 'Publicis Viewability',
  OUT_OF_VIEW_ADS: 'Out of View Ads',
  INVALID_TRAFFIC: 'Invalid Traffic',
  INVALID_TRAFFIC_BY_TYPE: 'Invalid Traffic By Type',
  FAILURE_REASONS: 'Failure Reasons',
  FAILURE_RISK_CATEGORIES: 'Failure Risk Categories',
  BLOCKED_REASONS: 'Blocked Reasons',
  BLOCKED_RISK_CATEGORIES: 'Blocked Risk Categories',
  VIDEO: 'Video',
};

// IMPORTANT!!! READ ME BEFORE UPDATING CONFIG
// if updating filters, make sure to update the formatFilter function above
// if updating main metrics... make sure to update the formatComparisonKeys function above

export const reportsDashboardConfig = {
  mainData: {
    graph: 'graph20.sql',
  },
  totalRevenue: {
    graph: 'graph84.sql',
  },
  // these are static filters requiring no props
  // NOTE: MENUTITLE MUST MATCH OBJECT KEY
  filters: {
    Formats: {
      menuTitle: 'Formats',
      list: {
        '001': {
          ID: '001',
          title: 'Display',
          selected: false,
          values: ['111', '221', '121', '231', '131'],
        },
        '002': {
          ID: '002',
          title: 'Video',
          selected: false,
          values: ['112', '222', '122', '232', '132'],
        },
      },
      singularString: 'Format',
      paramKey: 'formats',
    },
    'Device Types': {
      menuTitle: 'Device Types',
      list: {
        101: {
          ID: '101',
          title: 'Desktop',
          selected: false,
          values: ['111', '112'],
        },
        102: {
          ID: '102',
          title: 'Mobile',
          selected: false,
          values: ['221', '222', '121', '122'],
        },
        103: {
          ID: '103',
          title: 'Tablet',
          selected: false,
          values: ['231', '232', '131', '132'],
        },
      },
      singularString: 'Device Type',
      paramKey: 'devicetypes',
    },
  } as IReportsDashboardFilters,
  // NOTE: LABEL MUST MATCH OBJECT KEY
  // when adding new groupBys, make sure to add the keys to ITableGroupByKey
  groupBys: {
    Advertiser: {
      label: 'Advertiser',
      pluralLabel: 'Advertisers',
      apiKey: 'AdvertiserName',
      displayKey: 'AdvertiserName',
      requestKey: 'advertisers',
      matchKey: 'Advertisers',
      isSelectable: true,
      isTableControllingSort: true,
      isViewBy: true,
      isDefault: true,
      drillPath: [],
      subheader: 'Delivery',
    },
    Order: {
      label: 'Order',
      pluralLabel: 'Orders',
      apiKey: 'OrderName',
      displayKey: 'OrderName',
      requestKey: 'OrderID',
      matchKey: 'Orders',
      isSelectable: true,
      isTableControllingSort: true,
      isViewBy: true,
      drillPath: [],
      subheader: 'Delivery',
    },
    'Line Item': {
      label: 'Line Item',
      pluralLabel: 'Line Items',
      apiKey: 'LineItemName',
      displayKey: 'LineItemName',
      requestKey: 'LineItemID',
      matchKey: 'Line_Items',
      isSelectable: false,
      isTableControllingSort: true,
      isViewBy: true,
      drillPath: [],
      subheader: 'Delivery',
    },
    Creative: {
      label: 'Creative',
      pluralLabel: 'Creatives',
      apiKey: 'CreativeName',
      displayKey: 'CreativeName',
      requestKey: 'CreativeID',
      matchKey: 'Creatives',
      isSelectable: false,
      isTableControllingSort: true,
      isViewBy: true,
      drillPath: [],
      subheader: 'Delivery',
    },
    'Ad Unit': {
      label: 'Ad Unit',
      pluralLabel: 'Ad Units',
      apiKey: 'AdUnitName',
      displayKey: 'AdUnitName',
      requestKey: 'AdUnitID',
      matchKey: 'Ad_Units',
      isSelectable: false,
      isTableControllingSort: true,
      isViewBy: true,
      drillPath: [],
      subheader: 'Inventory',
    },
    Size: {
      label: 'Size',
      pluralLabel: 'Sizes',
      apiKey: 'AdSize',
      displayKey: 'AdSize',
      requestKey: 'Sizes',
      matchKey: 'Sizes',
      isSelectable: false,
      isTableControllingSort: true,
      isViewBy: true,
      drillPath: [],
      subheader: 'Inventory',
    },
    'Traffic Source': {
      label: 'Traffic Source',
      pluralLabel: 'Traffic Sources',
      apiKey: 'Custom1Name',
      displayKey: 'Custom1Name',
      requestKey: 'Traffic_Sources',
      matchKey: 'Traffic_Sources',
      isSelectable: false,
      isTableControllingSort: true,
      isViewBy: true,
      drillPath: [],
      subheader: 'Inventory',
    },
    Tile: {
      label: 'Tile',
      pluralLabel: 'Tiles',
      apiKey: 'Custom2Name',
      displayKey: 'Custom2Name',
      requestKey: 'Tiles',
      matchKey: 'Tiles',
      isSelectable: false,
      isTableControllingSort: true,
      isViewBy: true,
      drillPath: [],
      subheader: 'Inventory',
    },
    Section: {
      label: 'Section',
      pluralLabel: 'Sections',
      apiKey: 'Custom3Name',
      displayKey: 'Custom3Name',
      requestKey: 'Sections',
      matchKey: 'Sections',
      isSelectable: false,
      isTableControllingSort: true,
      isViewBy: true,
      drillPath: [],
      subheader: 'Inventory',
    },
  } as Partial<ITableGroupBysForReportsDashboard>,
  mainMetrics: {
    // ORDER MATTERS HERE
    // NOTE: LABEL MUST MATCH OBJECT KEY
    // when adding new metrics, make sure to add the keys to ITableMetricKey
    'Total Tracked Ads': {
      label: 'Total Tracked Ads',
      apiKey: 'TotalImps',
      requestKey: 'TotalImps',
      valueType: valueTypes.INTEGER,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
      subheader: CUSTOMIZED_METRICS_CATEGORIES.SUMMARY,
    },
    'Rendered Impressions': {
      label: 'Rendered Impressions',
      apiKey: 'RenderedImps',
      requestKey: 'RenderedImps',
      valueType: valueTypes.INTEGER,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
      subheader: CUSTOMIZED_METRICS_CATEGORIES.SUMMARY,
    },
    'Invalid Traffic Ads': {
      label: 'Invalid Traffic Ads',
      apiKey: 'IVTAds',
      requestKey: 'IVTAds',
      valueType: valueTypes.INTEGER,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
      subheader: CUSTOMIZED_METRICS_CATEGORIES.SUMMARY,
    },
    'Measured Ads': {
      label: 'Measured Ads',
      apiKey: 'MeasuredAds',
      requestKey: 'MeasuredAds',
      valueType: valueTypes.INTEGER,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
      subheader: CUSTOMIZED_METRICS_CATEGORIES.SUMMARY,
    },
    'Viewable Impressions': {
      label: 'Viewable Impressions',
      apiKey: 'ViewableImpression',
      requestKey: 'ViewableImpression',
      valueType: valueTypes.INTEGER,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
      subheader: CUSTOMIZED_METRICS_CATEGORIES.SUMMARY,
    },
    'Blocked Ads': {
      label: 'Blocked Ads',
      apiKey: 'AdBlockCount',
      requestKey: 'AdBlockCount',
      valueType: valueTypes.INTEGER,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
      subheader: CUSTOMIZED_METRICS_CATEGORIES.SUMMARY,
    },
    'Render Rate': {
      label: 'Render Rate',
      apiKey: 'RenderRate',
      requestKey: 'RenderRate',
      valueType: valueTypes.INTEGER,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
      subheader: CUSTOMIZED_METRICS_CATEGORIES.SUMMARY,
    },
    'Invalid Traffic Rate': {
      label: 'Invalid Traffic Rate',
      apiKey: 'IVTRate',
      requestKey: 'IVTRate',
      valueType: valueTypes.INTEGER,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
      subheader: CUSTOMIZED_METRICS_CATEGORIES.SUMMARY,
    },
    'Measured Rate': {
      label: 'Measured Rate',
      apiKey: 'MeasuredRate',
      requestKey: 'MeasuredRate',
      valueType: valueTypes.INTEGER,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
      subheader: CUSTOMIZED_METRICS_CATEGORIES.SUMMARY,
    },
    'Viewability Rate': {
      label: 'Viewability Rate',
      apiKey: 'ViewabilityRate',
      requestKey: 'ViewabilityRate',
      valueType: valueTypes.INTEGER,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
      subheader: CUSTOMIZED_METRICS_CATEGORIES.SUMMARY,
    },
    'Blocked Rate': {
      label: 'Blocked Rate',
      apiKey: 'AdBlockRate',
      requestKey: 'AdBlockRate',
      valueType: valueTypes.INTEGER,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
      subheader: CUSTOMIZED_METRICS_CATEGORIES.SUMMARY,
    },
    'GroupM Display Impressions': {
      label: 'GroupM Display Impressions',
      apiKey: 'GroupmFullyInViewDisplayImps',
      requestKey: 'GroupmFullyInViewDisplayImps',
      valueType: valueTypes.INTEGER,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
      subheader: CUSTOMIZED_METRICS_CATEGORIES.GROUPM_VIEWABILITY,
    },
    'GroupM Video Impressions': {
      label: 'GroupM Video Impressions',
      apiKey: 'GroupmFullyInViewVideoImps',
      requestKey: 'GroupmFullyInViewVideoImps',
      valueType: valueTypes.INTEGER,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
      subheader: CUSTOMIZED_METRICS_CATEGORIES.GROUPM_VIEWABILITY,
    },
    'Total Billable Impressions': {
      label: 'Total Billable Impressions',
      apiKey: 'TotalBillableImps',
      requestKey: 'TotalBillableImps',
      valueType: valueTypes.INTEGER,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
      subheader: CUSTOMIZED_METRICS_CATEGORIES.GROUPM_VIEWABILITY,
    },
    'GroupM Display Viewable Rate': {
      label: 'GroupM Display Viewable Rate',
      apiKey: 'GroupmDisplayViewableRate',
      requestKey: 'GroupmDisplayViewableRate',
      valueType: valueTypes.INTEGER,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
      subheader: CUSTOMIZED_METRICS_CATEGORIES.GROUPM_VIEWABILITY,
    },
    'GroupM Video Viewable Rate': {
      label: 'GroupM Video Viewable Rate',
      apiKey: 'GroupmVideoViewableRate',
      requestKey: 'GroupmVideoViewableRate',
      valueType: valueTypes.INTEGER,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
      subheader: CUSTOMIZED_METRICS_CATEGORIES.GROUPM_VIEWABILITY,
    },
    'Sitting Duck Bot Impressions': {
      label: 'Sitting Duck Bot Impressions',
      apiKey: 'SittingDuckBotImps',
      requestKey: 'SittingDuckBotImps',
      valueType: valueTypes.INTEGER,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
      subheader: CUSTOMIZED_METRICS_CATEGORIES.INVALID_TRAFFIC,
    },
    'Standard Bot Impressions': {
      label: 'Standard Bot Impressions',
      apiKey: 'StandardBotImps',
      requestKey: 'StandardBotImps',
      valueType: valueTypes.INTEGER,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
      subheader: CUSTOMIZED_METRICS_CATEGORIES.INVALID_TRAFFIC,
    },
    'Volunteer Bot Impressions': {
      label: 'Volunteer Bot Impressions',
      apiKey: 'VolunteerBotImps',
      requestKey: 'VolunteerBotImps',
      valueType: valueTypes.INTEGER,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
      subheader: CUSTOMIZED_METRICS_CATEGORIES.INVALID_TRAFFIC,
    },
    'Profile Bot Impressions': {
      label: 'Profile Bot Impressions',
      apiKey: 'ProfileBotImps',
      requestKey: 'ProfileBotImps',
      valueType: valueTypes.INTEGER,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
      subheader: CUSTOMIZED_METRICS_CATEGORIES.INVALID_TRAFFIC,
    },
    'Masked Bot Impressions': {
      label: 'Masked Bot Impressions',
      apiKey: 'MaskedBotImps',
      requestKey: 'MaskedBotImps',
      valueType: valueTypes.INTEGER,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
      subheader: CUSTOMIZED_METRICS_CATEGORIES.INVALID_TRAFFIC,
    },
    'Nomadic Bot Impressions': {
      label: 'Nomadic Bot Impressions',
      apiKey: 'NomadicBotImps',
      requestKey: 'NomadicBotImps',
      valueType: valueTypes.INTEGER,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
      subheader: CUSTOMIZED_METRICS_CATEGORIES.INVALID_TRAFFIC,
    },
    'Other Bot Impressions': {
      label: 'Other Bot Impressions',
      apiKey: 'OtherBotImps',
      requestKey: 'OtherBotImps',
      valueType: valueTypes.INTEGER,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
      subheader: CUSTOMIZED_METRICS_CATEGORIES.INVALID_TRAFFIC,
    },
    'Failed Adult Ads': {
      label: 'Failed Adult Ads',
      apiKey: 'FailedAdultAds',
      requestKey: 'FailedAdultAds',
      valueType: valueTypes.INTEGER,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
      subheader: CUSTOMIZED_METRICS_CATEGORIES.FAILURE_RISK_CATEGORIES,
    },
    'Failed Alcohol Ads': {
      label: 'Failed Alcohol Ads',
      apiKey: 'FailedAlcoholAds',
      requestKey: 'FailedAlcoholAds',
      valueType: valueTypes.INTEGER,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
      subheader: CUSTOMIZED_METRICS_CATEGORIES.FAILURE_RISK_CATEGORIES,
    },
    'Failed Hate Speech Ads': {
      label: 'Failed Hate Speech Ads',
      apiKey: 'FailedHateSpeechAds',
      requestKey: 'FailedHateSpeechAds',
      valueType: valueTypes.INTEGER,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
      subheader: CUSTOMIZED_METRICS_CATEGORIES.FAILURE_RISK_CATEGORIES,
    },
    'Failed Illegal Downloads Ads': {
      label: 'Failed Illegal Downloads Ads',
      apiKey: 'FailedIllegalDownloadsAds',
      requestKey: 'FailedIllegalDownloadsAds',
      valueType: valueTypes.INTEGER,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
      subheader: CUSTOMIZED_METRICS_CATEGORIES.FAILURE_RISK_CATEGORIES,
    },
    'Failed Drugs Ads': {
      label: 'Failed Drugs Ads',
      apiKey: 'FailedDrugsAds',
      requestKey: 'FailedDrugsAds',
      valueType: valueTypes.INTEGER,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
      subheader: CUSTOMIZED_METRICS_CATEGORIES.FAILURE_RISK_CATEGORIES,
    },
    'Failed Offensive Language Ads': {
      label: 'Failed Offensive Language Ads',
      apiKey: 'FailedOffensiveLanguageAds',
      requestKey: 'FailedOffensiveLanguageAds',
      valueType: valueTypes.INTEGER,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
      subheader: CUSTOMIZED_METRICS_CATEGORIES.FAILURE_RISK_CATEGORIES,
    },
    'Failed Violence Ads': {
      label: 'Failed Violence Ads',
      apiKey: 'FailedViolenceAds',
      requestKey: 'FailedViolenceAds',
      valueType: valueTypes.INTEGER,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
      subheader: CUSTOMIZED_METRICS_CATEGORIES.FAILURE_RISK_CATEGORIES,
    },
    'Refresh Ads': {
      label: 'Refresh Ads',
      apiKey: 'RefreshImps',
      requestKey: 'RefreshImps',
      valueType: valueTypes.INTEGER,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
      subheader: CUSTOMIZED_METRICS_CATEGORIES.AD_REFRESH,
    },
    'IAS Refreshed Ads': {
      label: 'IAS Refreshed Ads',
      apiKey: 'RefreshThresholdEvents',
      requestKey: 'RefreshThresholdEvents',
      valueType: valueTypes.INTEGER,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
      subheader: CUSTOMIZED_METRICS_CATEGORIES.AD_REFRESH,
    },
    'Refreshed Ad Rate': {
      label: 'Refreshed Ad Rate',
      apiKey: 'RefreshedAdRate',
      requestKey: 'RefreshedAdRate',
      valueType: valueTypes.INTEGER,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
      subheader: CUSTOMIZED_METRICS_CATEGORIES.AD_REFRESH,
    },
    'IAS Refreshed Ad Rate': {
      label: 'IAS Refreshed Ad Rate',
      apiKey: 'IASRefreshedAdRate',
      requestKey: 'IASRefreshedAdRate',
      valueType: valueTypes.INTEGER,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
      subheader: CUSTOMIZED_METRICS_CATEGORIES.AD_REFRESH,
    },
    'Publicis Fully In View Display Impressions': {
      label: 'Publicis Fully In View Display Impressions',
      apiKey: 'PublicisFullyInViewDisplayImps',
      requestKey: 'PublicisFullyInViewDisplayImps',
      valueType: valueTypes.INTEGER,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
      subheader: CUSTOMIZED_METRICS_CATEGORIES.PUBLICIS_VIEWABILITY,
    },
    'Publicis Fully In View Video Impressions': {
      label: 'Publicis Fully In View Video Impressions',
      apiKey: 'PublicisFullyInViewVideoImps',
      requestKey: 'PublicisFullyInViewVideoImps',
      valueType: valueTypes.INTEGER,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
      subheader: CUSTOMIZED_METRICS_CATEGORIES.PUBLICIS_VIEWABILITY,
    },
    'Publicis Display Viewable Rate': {
      label: 'Publicis Display Viewable Rate',
      apiKey: 'PublicisDisplayViewableRate',
      requestKey: 'PublicisDisplayViewableRate',
      valueType: valueTypes.INTEGER,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
      subheader: CUSTOMIZED_METRICS_CATEGORIES.PUBLICIS_VIEWABILITY,
    },
    'Publicis Video Viewable Rate': {
      label: 'Publicis Video Viewable Rate',
      apiKey: 'PublicisVideoViewableRate',
      requestKey: 'PublicisVideoViewableRate',
      valueType: valueTypes.INTEGER,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
      subheader: CUSTOMIZED_METRICS_CATEGORIES.PUBLICIS_VIEWABILITY,
    },
    'Blocked by Brand Safety': {
      label: 'Blocked by Brand Safety',
      apiKey: 'BlockedContentImps',
      requestKey: 'BlockedContentImps',
      valueType: valueTypes.INTEGER,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
      subheader: CUSTOMIZED_METRICS_CATEGORIES.BLOCKED_REASONS,
    },
    'Blocked by Brand Suitability': {
      label: 'Blocked by Brand Suitability',
      apiKey: 'BlockedSuitabilityImps',
      requestKey: 'BlockedSuitabilityImps',
      valueType: valueTypes.INTEGER,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
      subheader: CUSTOMIZED_METRICS_CATEGORIES.BLOCKED_REASONS,
    },
    'Blocked by Custom Keyword Exclusion List': {
      label: 'Blocked by Custom Keyword Exclusion List',
      apiKey: 'BlockedKeywordImps',
      requestKey: 'BlockedKeywordImps',
      valueType: valueTypes.INTEGER,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
      subheader: CUSTOMIZED_METRICS_CATEGORIES.BLOCKED_REASONS,
    },
    'Blocked by Custom URL/App Exclusion List': {
      label: 'Blocked by Custom URL/App Exclusion List',
      apiKey: 'BlockedCustomListImps',
      requestKey: 'BlockedCustomListImps',
      valueType: valueTypes.INTEGER,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
      subheader: CUSTOMIZED_METRICS_CATEGORIES.BLOCKED_REASONS,
    },
    'Blocked by Geo': {
      label: 'Blocked by Geo',
      apiKey: 'BlockedGeoImps',
      requestKey: 'BlockedGeoImps',
      valueType: valueTypes.INTEGER,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
      subheader: CUSTOMIZED_METRICS_CATEGORIES.BLOCKED_REASONS,
    },
    'Blocked by Invalid Traffic': {
      label: 'Blocked by Invalid Traffic',
      apiKey: 'BlockedSuspiciousImps',
      requestKey: 'BlockedSuspiciousImps',
      valueType: valueTypes.INTEGER,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
      subheader: CUSTOMIZED_METRICS_CATEGORIES.BLOCKED_REASONS,
    },
    'Blocked by Invisible URL': {
      label: 'Blocked by Invisible URL',
      apiKey: 'BlockedSeeThroughImps',
      requestKey: 'BlockedSeeThroughImps',
      valueType: valueTypes.INTEGER,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
      subheader: CUSTOMIZED_METRICS_CATEGORIES.BLOCKED_REASONS,
    },
    'Blocked by Target Language Mismatch': {
      label: 'Blocked by Target Language Mismatch',
      apiKey: 'BlockedLanguageImps',
      requestKey: 'BlockedLanguageImps',
      valueType: valueTypes.INTEGER,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
      subheader: CUSTOMIZED_METRICS_CATEGORIES.BLOCKED_REASONS,
    },
    'In View At Page Open Rate': {
      label: 'In View At Page Open Rate',
      apiKey: 'InViewAtPageOpenRate',
      requestKey: 'InViewAtPageOpenRate',
      valueType: valueTypes.INTEGER,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
      subheader: CUSTOMIZED_METRICS_CATEGORIES.EXPOSURE,
    },
    'In View > 5s Rate': {
      label: 'In View > 5s Rate',
      apiKey: 'InViewOver5sRate',
      requestKey: 'InViewOver5sRate',
      valueType: valueTypes.INTEGER,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
      subheader: CUSTOMIZED_METRICS_CATEGORIES.EXPOSURE,
    },
    'In View > 15s Rate': {
      label: 'In View > 15s Rate',
      apiKey: 'InViewOver15sRate',
      requestKey: 'InViewOver15sRate',
      valueType: valueTypes.INTEGER,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
      subheader: CUSTOMIZED_METRICS_CATEGORIES.EXPOSURE,
    },
    'Not Enough Time': {
      label: 'Not Enough Time',
      apiKey: 'MrcOvrNetImps',
      requestKey: 'MrcOvrNetImps',
      valueType: valueTypes.INTEGER,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
      subheader: CUSTOMIZED_METRICS_CATEGORIES.OUT_OF_VIEW_ADS,
    },
    'Not Enough Pixels': {
      label: 'Not Enough Pixels',
      apiKey: 'MrcOvrNepImps',
      requestKey: 'MrcOvrNepImps',
      valueType: valueTypes.INTEGER,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
      subheader: CUSTOMIZED_METRICS_CATEGORIES.OUT_OF_VIEW_ADS,
    },
    'Never On Screen': {
      label: 'Never On Screen',
      apiKey: 'MrcOvrNivImps',
      requestKey: 'MrcOvrNivImps',
      valueType: valueTypes.INTEGER,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
      subheader: CUSTOMIZED_METRICS_CATEGORIES.OUT_OF_VIEW_ADS,
    },
    'Out Of Focus': {
      label: 'Out Of Focus',
      apiKey: 'MrcOvrOofImps',
      requestKey: 'MrcOvrOofImps',
      valueType: valueTypes.INTEGER,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
      subheader: CUSTOMIZED_METRICS_CATEGORIES.OUT_OF_VIEW_ADS,
    },
    'Video Never Started': {
      label: 'Video Never Started',
      apiKey: 'MrcOvrNsImps',
      requestKey: 'MrcOvrNsImps',
      valueType: valueTypes.INTEGER,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
      subheader: CUSTOMIZED_METRICS_CATEGORIES.OUT_OF_VIEW_ADS,
    },
    'Other Out Of View': {
      label: 'Other Out Of View',
      apiKey: 'MrcOvrUnkImps',
      requestKey: 'MrcOvrUnkImps',
      valueType: valueTypes.INTEGER,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
      subheader: CUSTOMIZED_METRICS_CATEGORIES.OUT_OF_VIEW_ADS,
    },
    'Failed for Brand Safety': {
      label: 'Failed for Brand Safety',
      apiKey: 'FailedContentImps',
      requestKey: 'FailedContentImps',
      valueType: valueTypes.INTEGER,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
      subheader: CUSTOMIZED_METRICS_CATEGORIES.FAILURE_REASONS,
    },
    'Failed for Brand Suitability': {
      label: 'Failed for Brand Suitability',
      apiKey: 'FailedSuitabilityImps',
      requestKey: 'FailedSuitabilityImps',
      valueType: valueTypes.INTEGER,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
      subheader: CUSTOMIZED_METRICS_CATEGORIES.FAILURE_REASONS,
    },
    'Failed for Custom Keyword Exclusion List': {
      label: 'Failed for Custom Keyword Exclusion List',
      apiKey: 'FailedKeywordImps',
      requestKey: 'FailedKeywordImps',
      valueType: valueTypes.INTEGER,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
      subheader: CUSTOMIZED_METRICS_CATEGORIES.FAILURE_REASONS,
    },
    'Failed for Custom URL/App Exclusion List': {
      label: 'Failed for Custom URL/App Exclusion List',
      apiKey: 'FailedCustomListImps',
      requestKey: 'FailedCustomListImps',
      valueType: valueTypes.INTEGER,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
      subheader: CUSTOMIZED_METRICS_CATEGORIES.FAILURE_REASONS,
    },
    'Failed for Geo': {
      label: 'Failed for Geo',
      apiKey: 'FailedGeoImps',
      requestKey: 'FailedGeoImps',
      valueType: valueTypes.INTEGER,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
      subheader: CUSTOMIZED_METRICS_CATEGORIES.FAILURE_REASONS,
    },
    'Failed for Invalid Traffic': {
      label: 'Failed for Invalid Traffic',
      apiKey: 'FailedSuspiciousImps',
      requestKey: 'FailedSuspiciousImps',
      valueType: valueTypes.INTEGER,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
      subheader: CUSTOMIZED_METRICS_CATEGORIES.FAILURE_REASONS,
    },
    'Failed for Invisible URL': {
      label: 'Failed for Invisible URL',
      apiKey: 'FailedSeeThroughImps',
      requestKey: 'FailedSeeThroughImps',
      valueType: valueTypes.INTEGER,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
      subheader: CUSTOMIZED_METRICS_CATEGORIES.FAILURE_REASONS,
    },
    'Failed for Target Language Mismatch': {
      label: 'Failed for Target Language Mismatch',
      apiKey: 'FailedLanguageImps',
      requestKey: 'FailedLanguageImps',
      valueType: valueTypes.INTEGER,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
      subheader: CUSTOMIZED_METRICS_CATEGORIES.FAILURE_REASONS,
    },
    'Blocked Adult Ads': {
      label: 'Blocked Adult Ads',
      apiKey: 'BlockedContentAdultImps',
      requestKey: 'BlockedContentAdultImps',
      valueType: valueTypes.INTEGER,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
      subheader: CUSTOMIZED_METRICS_CATEGORIES.BLOCKED_RISK_CATEGORIES,
    },
    'Blocked Alcohol Ads': {
      label: 'Blocked Alcohol Ads',
      apiKey: 'BlockedContentAlcoholImps',
      requestKey: 'BlockedContentAlcoholImps',
      valueType: valueTypes.INTEGER,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
      subheader: CUSTOMIZED_METRICS_CATEGORIES.BLOCKED_RISK_CATEGORIES,
    },
    'Blocked Hate Speech Ads': {
      label: 'Blocked Hate Speech Ads',
      apiKey: 'BlockedContentHateImps',
      requestKey: 'BlockedContentHateImps',
      valueType: valueTypes.INTEGER,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
      subheader: CUSTOMIZED_METRICS_CATEGORIES.BLOCKED_RISK_CATEGORIES,
    },
    'Blocked Illegal Downloads Ads': {
      label: 'Blocked Illegal Downloads Ads',
      apiKey: 'BlockedContentDownloadsImps',
      requestKey: 'BlockedContentDownloadsImps',
      valueType: valueTypes.INTEGER,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
      subheader: CUSTOMIZED_METRICS_CATEGORIES.BLOCKED_RISK_CATEGORIES,
    },
    'Blocked Drugs Ads': {
      label: 'Blocked Drugs Ads',
      apiKey: 'BlockedContentDrugsImps',
      requestKey: 'BlockedContentDrugsImps',
      valueType: valueTypes.INTEGER,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
      subheader: CUSTOMIZED_METRICS_CATEGORIES.BLOCKED_RISK_CATEGORIES,
    },
    'Blocked Offensive Language Ads': {
      label: 'Blocked Offensive Language Ads',
      apiKey: 'BlockedContentOffensiveImps',
      requestKey: 'BlockedContentOffensiveImps',
      valueType: valueTypes.INTEGER,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
      subheader: CUSTOMIZED_METRICS_CATEGORIES.BLOCKED_RISK_CATEGORIES,
    },
    'Blocked Violence Ads': {
      label: 'Blocked Violence Ads',
      apiKey: 'BlockedContentViolenceImps',
      requestKey: 'BlockedContentViolenceImps',
      valueType: valueTypes.INTEGER,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
      subheader: CUSTOMIZED_METRICS_CATEGORIES.BLOCKED_RISK_CATEGORIES,
    },
    '% In View': {
      label: '% In View',
      apiKey: 'VideoInViewRate',
      requestKey: 'VideoInViewRate',
      valueType: valueTypes.INTEGER,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
      subheader: CUSTOMIZED_METRICS_CATEGORIES.VIDEO,
    },
    '% Out Of View': {
      label: '% Out Of View',
      apiKey: 'VideoOutOfViewRate',
      requestKey: 'VideoOutOfViewRate',
      valueType: valueTypes.INTEGER,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
      subheader: CUSTOMIZED_METRICS_CATEGORIES.VIDEO,
    },
    '% Ad Never Started': {
      label: '% Ad Never Started',
      apiKey: 'VideoNeverStartedRate',
      requestKey: 'VideoNeverStartedRate',
      valueType: valueTypes.INTEGER,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
      subheader: CUSTOMIZED_METRICS_CATEGORIES.VIDEO,
    },
    '% Completed': {
      label: '% Completed',
      apiKey: 'VideoCompletedRate',
      requestKey: 'VideoCompletedRate',
      valueType: valueTypes.INTEGER,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
      subheader: CUSTOMIZED_METRICS_CATEGORIES.VIDEO,
    },
    '% Completed In View': {
      label: '% Completed In View',
      apiKey: 'VideoCompletedInViewRate',
      requestKey: 'VideoCompletedInViewRate',
      valueType: valueTypes.INTEGER,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
      subheader: CUSTOMIZED_METRICS_CATEGORIES.VIDEO,
    },
    '% Muted': {
      label: '% Muted',
      apiKey: 'VideoMutedRate',
      requestKey: 'VideoMutedRate',
      valueType: valueTypes.INTEGER,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
      subheader: CUSTOMIZED_METRICS_CATEGORIES.VIDEO,
    },
    '% Full Screen': {
      label: '% Full Screen',
      apiKey: 'VideoFullScreenRate',
      requestKey: 'VideoFullScreenRate',
      valueType: valueTypes.INTEGER,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
      subheader: CUSTOMIZED_METRICS_CATEGORIES.VIDEO,
    },
    '% Click Through (CTR)': {
      label: '% Click Through (CTR)',
      apiKey: 'VideoClickThroughRate',
      requestKey: 'VideoClickThroughRate',
      valueType: valueTypes.INTEGER,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
      subheader: CUSTOMIZED_METRICS_CATEGORIES.VIDEO,
    },
    'General Invalid Traffic (GIVT) Ads': {
      label: 'General Invalid Traffic (GIVT) Ads',
      apiKey: 'FraudGivtImps',
      requestKey: 'FraudGivtImps',
      valueType: valueTypes.INTEGER,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
      subheader: CUSTOMIZED_METRICS_CATEGORIES.INVALID_TRAFFIC_BY_TYPE,
    },
    'Bots (SIVT) Ads': {
      label: 'Bots (SIVT) Ads',
      apiKey: 'FraudNhtImps',
      requestKey: 'FraudNhtImps',
      valueType: valueTypes.INTEGER,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
      subheader: CUSTOMIZED_METRICS_CATEGORIES.INVALID_TRAFFIC_BY_TYPE,
    },
    'Hidden Ads (SIVT)': {
      label: 'Hidden Ads (SIVT)',
      apiKey: 'FraudHaImps',
      requestKey: 'FraudHaImps',
      valueType: valueTypes.INTEGER,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
      subheader: CUSTOMIZED_METRICS_CATEGORIES.INVALID_TRAFFIC_BY_TYPE,
    },
    'Domain Spoofing (SIVT)': {
      label: 'Domain Spoofing (SIVT)',
      apiKey: 'FraudDsImps',
      requestKey: 'FraudDsImps',
      valueType: valueTypes.INTEGER,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
      subheader: CUSTOMIZED_METRICS_CATEGORIES.INVALID_TRAFFIC_BY_TYPE,
    },
    'Incentivized Browsing (RVI) Ads': {
      label: 'Incentivized Browsing (RVI) Ads',
      apiKey: 'FraudIbImps',
      requestKey: 'FraudIbImps',
      valueType: valueTypes.INTEGER,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
      subheader: CUSTOMIZED_METRICS_CATEGORIES.INVALID_TRAFFIC_BY_TYPE,
    },
    'Proxy Servers (RVI) Ads': {
      label: 'Proxy Servers (RVI) Ads',
      apiKey: 'FraudLspImps',
      requestKey: 'FraudLspImps',
      valueType: valueTypes.INTEGER,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
      subheader: CUSTOMIZED_METRICS_CATEGORIES.INVALID_TRAFFIC_BY_TYPE,
    },
    'Sophisticated Invalid Traffic Ads': {
      label: 'Sophisticated Invalid Traffic Ads',
      apiKey: 'SophisticatedInvalidTrafficAds',
      requestKey: 'SophisticatedInvalidTrafficAds',
      valueType: valueTypes.INTEGER,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
      subheader: CUSTOMIZED_METRICS_CATEGORIES.INVALID_TRAFFIC_BY_TYPE,
    },
    'Reduced Value Inventory Ads': {
      label: 'Reduced Value Inventory Ads',
      apiKey: 'ReducedValueInventoryAds',
      requestKey: 'ReducedValueInventoryAds',
      valueType: valueTypes.INTEGER,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
      subheader: CUSTOMIZED_METRICS_CATEGORIES.INVALID_TRAFFIC_BY_TYPE,
    },
    'General Invalid Traffic Rate': {
      label: 'General Invalid Traffic Rate',
      apiKey: 'GeneralInvalidTrafficRate',
      requestKey: 'GeneralInvalidTrafficRate',
      valueType: valueTypes.INTEGER,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
      subheader: CUSTOMIZED_METRICS_CATEGORIES.INVALID_TRAFFIC_BY_TYPE,
    },
    'Sophisticated Invalid Traffic Rate': {
      label: 'Sophisticated Invalid Traffic Rate',
      apiKey: 'SophisticatedInvalidTrafficRate',
      requestKey: 'SophisticatedInvalidTrafficRate',
      valueType: valueTypes.INTEGER,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
      subheader: CUSTOMIZED_METRICS_CATEGORIES.INVALID_TRAFFIC_BY_TYPE,
    },
    'Reduced Value Inventory Rate': {
      label: 'Reduced Value Inventory Rate',
      apiKey: 'ReducedValueInventoryRate',
      requestKey: 'ReducedValueInventoryRate',
      valueType: valueTypes.INTEGER,
      defaultOrderDirection: ORDER_DIRECTION.DESC,
      isSelectable: true,
      isTableControllingSort: true,
      subheader: CUSTOMIZED_METRICS_CATEGORIES.INVALID_TRAFFIC_BY_TYPE,
    },
  } as ITableMetrics,
  threshold: {
    'Show All Tracked Ads': 'all',
    'Min Tracked Ads:   1k': 1000,
    'Min Tracked Ads:  10k': 10000,
    'Min Tracked Ads: 100k': 100000,
    'Min Tracked Ads:   1m': 1000000,
  },
  table: {
    defaultRowsPerPage: 10,
    rowsPerPageOptions: [10, 25, 50],
    totalLimit: 100,
  },
} as IReportsDashboardConfig;
